/**
 * Contact Map -- Leaflet.js integration for the Kontakt page.
 *
 * Renders an interactive OpenStreetMap with CartoDB Positron (grayscale)
 * tiles, a custom accent-colored marker, and an address popup.
 * GDPR-friendly: zero third-party cookies, no Google services.
 *
 * @package Sansegynge
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

document.addEventListener('DOMContentLoaded', () => {
	initContactMap();
});

/**
 * Initialize the Leaflet map on the contact page.
 *
 * Reads lat/lng/zoom from data attributes on [data-contact-map],
 * configures grayscale tiles with CSS warm filter, and adds
 * a custom SVG marker with address popup.
 */
function initContactMap() {
	const mapContainer = document.querySelector('[data-contact-map]');
	if (!mapContainer) return;

	const lat = parseFloat(mapContainer.dataset.lat) || 55.8833;
	const lng = parseFloat(mapContainer.dataset.lng) || 9.3833;
	const zoom = parseInt(mapContainer.dataset.zoom, 10) || 13;

	// Initialize map with scroll-wheel zoom disabled (prevents accidental zoom)
	const map = L.map(mapContainer, {
		scrollWheelZoom: false,
		attributionControl: true,
	}).setView([lat, lng], zoom);

	// Enable scroll zoom only after clicking the map (better UX)
	map.once('click', () => {
		map.scrollWheelZoom.enable();
	});

	// CartoDB Positron grayscale tiles (GDPR-friendly, no cookies, no API key)
	L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
		attribution:
			'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
		subdomains: 'abcd',
		maxZoom: 19,
	}).addTo(map);

	// Custom SVG marker in accent color (#B5A081)
	const markerIcon = L.divIcon({
		className: 'sg-map-marker',
		html: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42" fill="none">
			<path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="#B5A081"/>
			<circle cx="16" cy="16" r="6" fill="white"/>
		</svg>`,
		iconSize: [32, 42],
		iconAnchor: [16, 42],
		popupAnchor: [0, -42],
	});

	// Add marker at Vonge location
	const marker = L.marker([lat, lng], { icon: markerIcon }).addTo(map);

	// Bind popup with Sansegynge address
	marker.bindPopup(`
		<div style="font-family: 'DM Sans Variable', sans-serif; font-size: 13px; line-height: 1.5; color: #2C2420;">
			<strong style="font-family: 'Cormorant Garamond Variable', serif; font-size: 16px; font-weight: 500;">Sansegynge</strong><br>
			Vongeskovvej 21<br>
			7173 Vonge
		</div>
	`);
}
